// ===== WMS Layout Designer Types =====

// --- Enums ---

export enum LayoutMode {
  DESIGN = 'DESIGN',
  VIEW = 'VIEW',
}

export enum ViewPerspective {
  BIRDS_EYE = 'BIRDS_EYE',
  RACK_FACE = 'RACK_FACE',
}

export enum ElementType {
  ZONE = 'ZONE',
  RACK = 'RACK',
  AISLE = 'AISLE',
  DOOR = 'DOOR',
  DOCK = 'DOCK',
  COLUMN = 'COLUMN',
  WALL = 'WALL',
  STAGING_AREA = 'STAGING_AREA',
  LABEL = 'LABEL',
}

export enum CanvasLayer {
  BACKGROUND = 'BACKGROUND',
  ZONES = 'ZONES',
  INFRASTRUCTURE = 'INFRASTRUCTURE',
  RACKS = 'RACKS',
  EQUIPMENT = 'EQUIPMENT',
  PATHS = 'PATHS',
  LABELS = 'LABELS',
  OVERLAYS = 'OVERLAYS',
  SELECTION = 'SELECTION',
}

export enum ZoneType {
  RECEIVING = 'RECEIVING',
  SHIPPING = 'SHIPPING',
  STORAGE = 'STORAGE',
  STAGING = 'STAGING',
  COLD = 'COLD',
  HAZMAT = 'HAZMAT',
  QUARANTINE = 'QUARANTINE',
  RETURNS = 'RETURNS',
  GENERAL = 'GENERAL',
}

export enum RackType {
  SELECTIVE = 'SELECTIVE',
  DRIVE_IN = 'DRIVE_IN',
  PUSH_BACK = 'PUSH_BACK',
  PALLET_FLOW = 'PALLET_FLOW',
  CANTILEVER = 'CANTILEVER',
  MEZZANINE = 'MEZZANINE',
  CARTON_FLOW = 'CARTON_FLOW',
}

export enum OverlayType {
  OCCUPANCY = 'OCCUPANCY',
  PICK_FREQUENCY = 'PICK_FREQUENCY',
  PUTAWAY_FREQUENCY = 'PUTAWAY_FREQUENCY',
  DEAD_STOCK = 'DEAD_STOCK',
  ACTIVITY = 'ACTIVITY',
  WORKERS = 'WORKERS',
  TASK_FLOW = 'TASK_FLOW',
  PICK_ROUTE = 'PICK_ROUTE',
  SLOTTING = 'SLOTTING',
  TRAFFIC_DENSITY = 'TRAFFIC_DENSITY',
}

// --- Geometry ---

export interface Position {
  x: number;
  y: number;
}

export interface Dimensions {
  width: number;
  height: number;
}

// --- Visual Properties (stored on existing entities) ---

export interface ZoneVisualProperties {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation?: number;
  fillColor?: string;
  opacity?: number;
}

export interface AisleVisualProperties {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation?: number;
  fillColor?: string;
}

export interface LocationVisualProperties {
  x: number;
  y: number;
  width: number;
  height: number;
}

// --- Canvas Configuration ---

export interface BackgroundImage {
  url: string;
  opacity: number;
  offsetX: number;
  offsetY: number;
  scale: number;
  locked: boolean;
}

export interface CanvasConfig {
  width: number;
  height: number;
  gridSize: number;
  snapToGrid: boolean;
  showGrid: boolean;
  showRulers: boolean;
  backgroundColor: string;
  unit: 'ft' | 'm';
  pixelsPerUnit: number;
  backgroundImage?: BackgroundImage;
}

export interface LayerConfig {
  id: CanvasLayer;
  name: string;
  visible: boolean;
  locked: boolean;
  opacity: number;
  order: number;
}

// --- Canvas Elements (discriminated union) ---

export interface BaseElement {
  id: string;
  type: ElementType;
  layer: CanvasLayer;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  locked: boolean;
  visible: boolean;
  opacity: number;
  name: string;
  metadata?: Record<string, unknown>;
}

export interface ZoneElement extends BaseElement {
  type: ElementType.ZONE;
  layer: CanvasLayer.ZONES;
  zoneType: ZoneType;
  fillColor: string;
  strokeColor: string;
  linkedZoneId?: string;
}

export interface RackElement extends BaseElement {
  type: ElementType.RACK;
  layer: CanvasLayer.RACKS;
  rackType: RackType;
  bays: number;
  levels: number;
  bayWidth: number;
  levelHeight: number;
  fillColor: string;
  strokeColor: string;
  linkedAisleId?: string;
}

export interface AisleElement extends BaseElement {
  type: ElementType.AISLE;
  layer: CanvasLayer.INFRASTRUCTURE;
  direction: 'horizontal' | 'vertical';
  fillColor: string;
}

export interface WallElement extends BaseElement {
  type: ElementType.WALL;
  layer: CanvasLayer.INFRASTRUCTURE;
  thickness: number;
  fillColor: string;
}

export interface DoorElement extends BaseElement {
  type: ElementType.DOOR;
  layer: CanvasLayer.INFRASTRUCTURE;
  doorType: 'standard' | 'roll_up' | 'emergency';
  fillColor: string;
}

export interface DockElement extends BaseElement {
  type: ElementType.DOCK;
  layer: CanvasLayer.INFRASTRUCTURE;
  dockNumber: string;
  dockType: 'inbound' | 'outbound' | 'both';
  fillColor: string;
}

export interface ColumnElement extends BaseElement {
  type: ElementType.COLUMN;
  layer: CanvasLayer.INFRASTRUCTURE;
  fillColor: string;
}

export interface StagingAreaElement extends BaseElement {
  type: ElementType.STAGING_AREA;
  layer: CanvasLayer.ZONES;
  purpose: 'inbound' | 'outbound' | 'general';
  fillColor: string;
}

export interface LabelElement extends BaseElement {
  type: ElementType.LABEL;
  layer: CanvasLayer.LABELS;
  text: string;
  fontSize: number;
  fontFamily: string;
  fontColor: string;
  textAlign: 'left' | 'center' | 'right';
}

export type VisualElement =
  | ZoneElement
  | RackElement
  | AisleElement
  | WallElement
  | DoorElement
  | DockElement
  | ColumnElement
  | StagingAreaElement
  | LabelElement;

// --- Layout Settings ---

export interface NamingConvention {
  zonePrefix: string;
  aislePrefix: string;
  rackPrefix: string;
  locationFormat: string;
  separator: string;
}

export interface LayoutSettings {
  namingConvention: NamingConvention;
  defaultUnit: 'ft' | 'm';
  autoSaveInterval: number;
}

// --- Warehouse Layout (main entity) ---

export interface WarehouseLayout {
  id: string;
  tenant_id: string;
  warehouse_id: string;
  name: string;
  description?: string;
  canvas_config: CanvasConfig;
  elements: VisualElement[];
  layer_config: LayerConfig[];
  naming_convention?: NamingConvention;
  metadata?: Record<string, unknown>;
  is_active: boolean;
  version: number;
  created_by?: string;
  updated_by?: string;
  created_at: string;
  updated_at: string;
}

// --- Phase 2: View Mode Types ---

export interface HeatMapConfig {
  type: OverlayType;
  minColor: string;
  maxColor: string;
  opacity: number;
  showLegend: boolean;
}

export interface HeatMapDataPoint {
  elementId: string;
  locationId?: string;
  bay?: number;
  value: number;
  normalizedValue: number;
}

export interface SearchResult {
  locationId: string;
  locationCode: string;
  elementId: string;
  bay?: number;
  x: number;
  y: number;
  sku?: string;
  itemName?: string;
  quantity?: number;
  lotNumber?: string;
}

export interface ActivityEvent {
  id: string;
  type: string;
  locationId: string;
  elementId: string;
  x: number;
  y: number;
  workerName: string;
  timestamp: string;
}

export interface WorkerPosition {
  workerId: string;
  workerName: string;
  x: number;
  y: number;
  currentZone?: string;
  currentTask?: string;
  lastUpdate: string;
}

export interface TaskFlow {
  taskId: string;
  taskType: string;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  workerName?: string;
  status: string;
}

export interface RackFaceData {
  rackId: string;
  rackName: string;
  rackType: RackType;
  bays: number;
  levels: number;
  bins: RackFaceBin[];
}

export interface RackFaceBin {
  bay: number;
  level: number;
  locationId: string;
  locationCode: string;
  status: 'AVAILABLE' | 'OCCUPIED' | 'RESERVED' | 'BLOCKED';
  occupancyPercent: number;
  sku?: string;
  itemName?: string;
  quantity?: number;
  lotNumber?: string;
}

export interface ElementDetail {
  elementId: string;
  elementType: ElementType;
  name: string;
  data: Record<string, unknown>;
}

export interface TimelineData {
  timestamp: string;
  heatValues: HeatMapDataPoint[];
  activityEvents: ActivityEvent[];
}

// --- Phase 4: Export/Import/Template Types ---

export interface LayoutExportPayload {
  schemaVersion: string;
  exportedAt: string;
  name: string;
  description?: string;
  canvas_config: Record<string, unknown>;
  elements: Record<string, unknown>[];
  layer_config?: Record<string, unknown>[];
  naming_convention?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}

export interface LayoutTemplate {
  id: string;
  tenant_id: string | null;
  name: string;
  description?: string;
  category: string;
  elements: Record<string, unknown>[];
  canvas_config: Record<string, unknown>;
  layer_config?: Record<string, unknown>[];
  thumbnail_url?: string;
  is_system: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// --- Phase 5C: Pick Route Visualization Types ---

export interface PickRouteSegment {
  sequence: number;
  taskId: string;
  taskNumber: string;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  fromLocationCode: string;
  toLocationCode: string;
  productSku: string | null;
  quantity: number | null;
  status: string;
  workerName: string | null;
}

export interface PickRouteStats {
  totalStops: number;
  completedStops: number;
  totalDistancePixels: number;
  totalDistanceUnits: number;
  estimatedTimeMinutes: number;
  unit: 'ft' | 'm';
}

export interface PickRouteResult {
  segments: PickRouteSegment[];
  stats: PickRouteStats;
}

export interface PickRouteFilters {
  groupId?: string;
  workerId?: string;
  status?: string;
}

// --- Phase 5D: Slotting Optimization Types ---

export interface AbcClassification {
  elementId: string;
  elementName: string;
  bay: number | null;
  category: 'A' | 'B' | 'C';
  pickFrequency: number;
  distanceToShipping: number;
  isOptimal: boolean;
}

export interface SlottingSuggestion {
  id: string;
  priority: 'HIGH' | 'MEDIUM' | 'LOW';
  fromElementId: string;
  fromElementName: string;
  fromBay: number | null;
  toElementId: string;
  toElementName: string;
  toBay: number | null;
  reason: string;
  estimatedSaving: number;
}

export interface SlottingStats {
  totalRacks: number;
  aItems: number;
  bItems: number;
  cItems: number;
  avgDistanceToShipping: number;
  potentialTravelReduction: number;
  unit: 'ft' | 'm';
}

export interface SlottingAnalysisResult {
  classification: AbcClassification[];
  suggestions: SlottingSuggestion[];
  stats: SlottingStats;
}
